import { Fragment, useEffect, useState } from "react";
import { useInRouterContext, useNavigate } from "react-router-dom";

import { getPracticeId } from "../config/practice";
import {
  FAX_AI_PENDING_TEXT,
  faxAiDetailView,
  faxAiMatchHint,
  faxAiMatchIsUnique,
  faxAiUniquePatient,
  faxAiView,
} from "./faxAi";
import { faxAiDetail, faxDocumentId } from "./faxApi";

/*
 * The AI read of a fax in Communication > Fax (faxAi.js has the contract).
 * Quiet on purpose: a grey pill, a grey summary and a small grey "Needs: ..."
 * in the list; above the viewer the whole summary and "Needs: ..." in grey,
 * then one grey line with the single accent (the suggested chart link).
 */

// 10-06 (prompt fax-v2): what the clinic is asked to do, e.g. "Sign and
// return CPAP order by 10/10". Not shown when nothing is asked.
const NEEDS = "Needs:";

// List row, under the sender: the label pill on its own line, the summary
// under it (at most 2 lines, all of it on hover), then "Needs: <action>" on
// one small line (all of it on hover), or "Summarizing...".
// 10-06: pill and summary used to share one truncated line, and in the
// narrow list the pill left the summary no room at all.
export function FaxAiListLine({ row, active = false }) {
  const view = faxAiView(row);
  if (!view) return null;
  const grey = active ? "text-[#FFFFFFCC]" : "text-[#7E7E7E]";
  if (view.pending) {
    return (
      <div className={`w-full truncate font-inter text-[10px] italic ${grey}`} data-testid="fax-ai-pending">
        {FAX_AI_PENDING_TEXT}
      </div>
    );
  }
  if (!view.label && !view.summary && !view.action) return null;
  return (
    <div className="flex w-full min-w-0 flex-col items-start gap-0.5" data-testid="fax-ai-line">
      {view.label && (
        <span
          className={`max-w-full truncate rounded-full px-2 py-[1px] font-inter text-[10px] font-medium leading-4 ${
            active ? "bg-white/20 text-white" : "bg-[#F2F4F7] text-[#475467]"
          }`}
          title={view.label}
          data-testid="fax-ai-label"
        >
          {view.label}
        </span>
      )}
      {view.summary && (
        <div
          className={`line-clamp-2 w-full break-words text-start font-inter text-[11px] leading-[14px] ${grey}`}
          title={view.summary}
          data-testid="fax-ai-summary"
        >
          {view.summary}
        </div>
      )}
      {view.action && (
        <div
          className={`w-full truncate text-start font-inter text-[10px] leading-[13px] ${grey}`}
          title={`${NEEDS} ${view.action}`}
          data-testid="fax-ai-action"
        >
          {NEEDS} {view.action}
        </div>
      )}
    </div>
  );
}

const who = (p) => (p.dob ? `${p.name} · ${p.dob}` : p.name);

// Opens the patient on the dashboard, the way the schedule's patient photo
// does (brainycarehome reads state.fromPatientId). Navigation only.
function RoutedPatientLink({ patient }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="text-[#50869A] hover:underline focus:underline focus:outline-none"
      onClick={() => navigate("/landing", { state: { fromPatientId: patient.patientid } })}
      data-testid="fax-ai-patient-link"
    >
      {who(patient)}
    </button>
  );
}

function PatientLink({ patient }) {
  // Outside a router (never in the app) the name shows without the link.
  return useInRouterContext() ? <RoutedPatientLink patient={patient} /> : <span>{who(patient)}</span>;
}

/*
 * v2 lists only the suggested chart's id; its name and DOB come from
 * GET fax/{fax_id}/ai, once per fax and suggestion (kept here, so the
 * minute re-reads of the list never ask again). A failed read shows nothing
 * and is asked again next time the fax is opened.
 */
const DETAIL_CACHE_MAX = 100;
const detailCache = new Map();
// One request per key at a time: the viewer line and Send to Athena both
// read the open fax's chart and share the answer.
const detailInflight = new Map();

export function clearFaxAiDetailCache() {
  detailCache.clear();
  detailInflight.clear();
}

function loadDetail(key, row) {
  const asked = detailInflight.get(key);
  if (asked) return asked;
  const p = faxAiDetail(row).then((data) => {
    const value = faxAiDetailView(data);
    if (value) {
      if (detailCache.size >= DETAIL_CACHE_MAX) detailCache.delete(detailCache.keys().next().value);
      detailCache.set(key, value);
    }
    return value;
  });
  detailInflight.set(key, p);
  const settled = () => {
    if (detailInflight.get(key) === p) detailInflight.delete(key);
  };
  p.then(settled, settled);
  return p;
}

function detailKey(row, ai) {
  const id = faxDocumentId(row);
  if (id === null) return null;
  return [getPracticeId(), id, ai?.match_status, ai?.suggested_patientid].map((v) => String(v ?? "")).join(":");
}

function useFaxAiDetail(row, wanted) {
  const ai = row && (row.ai || row.fax_ai);
  const key = wanted ? detailKey(row, ai) : null;
  const [state, setState] = useState(() => ({ key, value: key ? detailCache.get(key) || null : null }));
  useEffect(() => {
    if (!key || detailCache.has(key)) return undefined;
    let live = true;
    setState({ key, value: null });
    loadDetail(key, row)
      .then((value) => {
        if (live) setState({ key, value });
      })
      .catch(() => {
        if (live) setState({ key, value: null });
      });
    return () => {
      live = false;
    };
    // `row` changes on every list re-read; the key says when to ask again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  if (!key) return null;
  if (detailCache.has(key)) return detailCache.get(key);
  // never another fax's answer while the new one loads
  return state.key === key ? state.value : null;
}

/*
 * The one chart Send to Athena pre-selects for this fax (faxAi.js
 * faxAiUniquePatient): { patientid, name, dob, confidence } or null. v2 rows
 * carry the id only, so this reads GET fax/{id}/ai like the viewer line does
 * (same cache, same request). null row: nothing is read.
 */
export function useFaxAiSuggestedPatient(row) {
  const view = faxAiView(row);
  const wanted = !!view && !view.pending && view.detail && faxAiMatchIsUnique(row);
  const detail = useFaxAiDetail(row, wanted);
  return faxAiUniquePatient(row, detail);
}

// Viewer header: the whole summary (grey), "Needs: <action>" (grey), then
// "Possibly: <name> · <DOB> (how close)" (several charts: each one, none
// picked), or "Summarizing..." while the fax is being read.
export function FaxAiViewerLine({ row }) {
  const view = faxAiView(row);
  const detail = useFaxAiDetail(row, !!view && !view.pending && view.detail);
  if (!view) return null;
  if (view.pending) {
    return (
      <div className="px-3 text-start text-xs italic text-[#7E7E7E]" data-testid="fax-ai-viewer-pending">
        {FAX_AI_PENDING_TEXT}
      </div>
    );
  }
  const fromDetail = !view.suggestions.length;
  const suggestions = fromDetail ? detail?.suggestions || [] : view.suggestions;
  if (!suggestions.length && !view.summary && !view.action) return null;
  const confidence = suggestions.length !== 1 ? null : fromDetail ? detail?.confidence ?? view.confidence : view.confidence;
  const hint = faxAiMatchHint(confidence);
  return (
    <div className="space-y-0.5" data-testid="fax-ai-viewer">
      {view.summary && (
        <p className="px-3 text-start text-xs text-[#7E7E7E]" data-testid="fax-ai-viewer-summary">
          {view.summary}
        </p>
      )}
      {view.action && (
        <p className="px-3 text-start text-xs text-[#7E7E7E]" data-testid="fax-ai-viewer-action">
          <span className="font-medium">{NEEDS}</span> {view.action}
        </p>
      )}
      {suggestions.length > 0 && (
        <div className="px-3 text-start text-xs text-[#7E7E7E]" data-testid="fax-ai-patient">
          Possibly:{" "}
          {suggestions.map((p, i) => (
            <Fragment key={String(p.patientid)}>
              {i > 0 && " or "}
              <PatientLink patient={p} />
            </Fragment>
          ))}
          {hint && <span data-testid="fax-ai-match-hint"> ({hint})</span>}
        </div>
      )}
    </div>
  );
}
